import { createAsyncThunk } from '@reduxjs/toolkit';
import apiCall from 'utils/api';
import { IInterview, IInterviewDetail, IUpdateInterviewPayload, IApiResponse, ICancelInterviewPayload, IUploadInterviewRecordingPayload } from './types';

const getErrorMessage = (error: any, defaultMessage: string) => {
  if (error.response && error.response.data) {
    const data = error.response.data;

    // Check for nested data object with specific field errors (prioritize over generic message)
    if (data.data && typeof data.data === 'object' && !Array.isArray(data.data)) {
      const nestedData = data.data;
      const keys = Object.keys(nestedData);
      if (keys.length > 0) {
        const firstKey = keys[0];
        const val = nestedData[firstKey];
        if (Array.isArray(val)) return `${val.join(', ')}`;
        if (typeof val === 'string') return val;
      }
    }

    if (typeof data === 'string') return data;

    // Check for specific error fields before generic message
    if (data.detail) return data.detail;
    if (data.non_field_errors) {
      if (Array.isArray(data.non_field_errors)) return data.non_field_errors.join(', ');
      return data.non_field_errors;
    }

    if (data.message) return data.message;

    // Fallback to top-level keys if no specific structure found
    const keys = Object.keys(data);
    if (keys.length > 0) {
      const firstKey = keys[0];
      const val = data[firstKey];
      if (Array.isArray(val)) return `${firstKey}: ${val.join(', ')}`;
      if (typeof val === 'string') return `${firstKey}: ${val}`;
    }
  }
  return defaultMessage;
};

export const scheduleInterview = createAsyncThunk(
  'interview/scheduleInterview',
  async (interviewData: Omit<IInterview, 'id' | 'status'>, { rejectWithValue }) => {
    try {
      const response = await apiCall<IApiResponse>('post', '/api/interview/schedule/', interviewData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to schedule interview'));
    }
  }
);

export const getInterviewDetail = createAsyncThunk(
  'interview/getInterviewDetail',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: IInterviewDetail[] }>('get', `/api/interview/detail/${id}/`);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch interview details'));
    }
  }
);

export const updateInterview = createAsyncThunk(
  'interview/updateInterview',
  async ({ interviewId, ...interviewData }: IUpdateInterviewPayload, { rejectWithValue }) => {
    try {
      const response = await apiCall<IApiResponse>(
        'patch',
        `/api/interview/update/${interviewId}/`,
        interviewData
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update interview'));
    }
  }
);

export const cancelInterview = createAsyncThunk(
  'interview/cancelInterview',
  async ({ interviewId }: ICancelInterviewPayload, { rejectWithValue }) => {
    try {
      const response = await apiCall<IApiResponse>(
        'patch',
        `/api/interview/cancel/${interviewId}/`,
        {} // No request body for cancel
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to cancel interview'));
    }
  }
);

export const uploadInterviewRecording = createAsyncThunk(
  'interview/uploadInterviewRecording',
  async ({ interviewId, file, remark }: IUploadInterviewRecordingPayload, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('remark', remark);

      const response = await apiCall<IApiResponse>(
        'post',
        `/api/interview/upload-recording/${interviewId}/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to upload recording'));
    }
  }
);

export const markInterviewAsViewed = createAsyncThunk(
  'interview/markInterviewAsViewed',
  async (interviewId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ message: string }>('post', `/api/interview/viewed/${interviewId}/`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to mark interview as viewed'));
    }
  }
);
